<template>
  <div class="claim-list">
    <!-- 费用报销（企业微信手机端） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="单号/事由/申请人" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <!-- 数据模式：显示当前模式，点一下在「列表模式 / 报表模式」之间切换 -->
        <div class="mode-chip" @click="switchMode()">{{ modeLabel }}</div>
        <mobile-condition-select v-model="searchInfo.expenseTypeId" :data="typeList"
                                 label-column="dictName" code-column="userCode" type="list"
                                 placeholder="费用类型" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.claimStatus" :data="statusOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="申请状态" @change="resetSearch"/>
      </div>
    </mobile-list-header>

    <!-- ==================== 列表模式：报销单列表，可点进详情 ==================== -->
    <mobile-list v-if="mode === 'list'" :loading="loading" :has-more="searchInfo.hasMore"
                 :query-list="queryList" :refresh="resetSearch" ref="mobileList">
      <list-card class="claim-item" v-for="item in dataList" :key="item.id" :label="item.claimReason" @click="toDetail(item)">
        <template #desc>
          <span class="type-tag" :class="'tag-' + typeTagType(item.expenseTypeId)">{{ typeName(item.expenseTypeId) }}</span>
          <span class="status-tag" :class="'tag-' + statusTagType(item.claimStatus)">{{ statusName(item.claimStatus) }}</span>
          <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
        </template>
        <data-column label="报销单号">{{ item.claimNo || "—" }}</data-column>
        <data-column label="报销金额">{{ money(item.claimAmount) }}</data-column>
        <data-column label="申请人">{{ item.applicantName || "—" }}</data-column>
        <data-column label="所属部门">{{ item.departTitle || "—" }}</data-column>
        <data-column v-if="item.partnerName" label="关联客户">{{ item.partnerName }}</data-column>
        <data-column label="发生日期">{{ dateOnly(item.occurDate) }}</data-column>
        <data-column label="审批状态">{{ approvalName(item.approvalStatus) }}</data-column>
      </list-card>
    </mobile-list>

    <!-- ==================== 报表模式：KPI + 费用类型分布，仅查看不可点 ==================== -->
    <div v-else class="report-body">
      <div class="stat-grid">
        <div class="stat-card">
          <div class="label">报销单数</div>
          <div class="value">{{ stat.claimCount || 0 }}<span class="unit">单</span></div>
        </div>
        <div class="stat-card">
          <div class="label">报销总金额</div>
          <div class="value money">{{ money(stat.totalAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">审批中</div>
          <div class="value warning">{{ stat.approvingCount || 0 }}<span class="unit">单</span></div>
        </div>
        <div class="stat-card">
          <div class="label">已通过</div>
          <div class="value success">{{ stat.passedCount || 0 }}<span class="unit">单</span></div>
        </div>
      </div>
      <!-- 口径说明：草稿不算发生额，但列表仍要展示，否则申请人没法继续改 -->
      <div class="tip-bar">
        口径说明：KPI 与费用类型分布只统计「已提交」的单据，草稿尚未提交、不计入发生额；
        列表仍会展示草稿，方便继续编辑与提交。
      </div>
      <!-- 费用类型分布：饼图（字典值后端不返回名称，这里关联 EXPENSE_TYPE 后再喂给图表） -->
      <div class="chart-card">
        <div class="section-title">费用类型分布（按金额）</div>
        <div ref="typeChart" class="chart-div"></div>
      </div>
    </div>

    <!-- 悬浮「新增报销」按钮：只有列表模式才显示（报表模式是只读统计） -->
    <mobile-float-button v-if="mode === 'list'" label="新增报销" icon="element-ui_plus" @click="toCreate"/>
  </div>
</template>

<script>

import * as echarts from "echarts";
import {markRaw} from "vue";
import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import MobileFloatButton from "@/components/qywx/button/MobileFloatButton.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import expenseClaimApi from "@/api/dz-crm/expense/expenseClaimApi";
import commonApi from "@/api/commonApi";

/** 申请状态：0草稿 100审批中 200通过 300驳回 */
const STATUS = {DRAFT: 0, APPROVING: 100, PASSED: 200, REJECTED: 300};
const STATUS_OPTIONS = [
  {value: STATUS.DRAFT, label: "草稿"},
  {value: STATUS.APPROVING, label: "审批中"},
  {value: STATUS.PASSED, label: "已通过"},
  {value: STATUS.REJECTED, label: "已驳回"},
];
const STATUS_TAG = {0: "info", 100: "warning", 200: "success", 300: "danger"};
/** 费用类型标签色，按字典顺序循环取，字典可增删所以不写死映射 */
const TYPE_COLORS = ["primary", "warning", "danger", "success", "info"];
/** 审批状态（工作流维度），与后端 ProcessFlowConstant.ApprovalStatus 对齐 */
const APPROVAL_OPTIONS = [
  {value: 0, label: "未开始"},
  {value: 100, label: "审批中"},
  {value: 200, label: "已中止"},
  {value: 300, label: "已完成"},
  {value: 400, label: "已撤回"},
  {value: 500, label: "已驳回"},
];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "EC",
  components: {
    YqIcon, DataColumn, ListCard, MobileFloatButton, MobileConditionSelect, MobileListHeader, MobileList
  },
  data() {
    return {
      // 数据模式：list 列表模式（报销单列表）/ report 报表模式（KPI + 费用类型分布）
      mode: "list",
      loading: false,
      searchInfo: {
        searchKey: "",
        expenseTypeId: "",
        claimStatus: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      stat: {},
      typeList: [],
      statusOptions: STATUS_OPTIONS,
      typeChart: null,
    };
  },
  computed: {
    // chip 上显示的是「当前」模式，点一下切到另一个
    modeLabel() {
      return this.mode === "list" ? "列表模式" : "报表模式";
    },
    typeRows() {
      return this.stat.typeRows || [];
    }
  },
  created() {
    // 费用类型是一致性字典（type = EXPENSE_TYPE），取值 userCode / 显示 dictName
    commonApi.queryUniqueDictionaryList("EXPENSE_TYPE").then(list => this.typeList = list || []);
  },
  unmounted() {
    if (this.typeChart) {
      this.typeChart.dispose();
      this.typeChart = null;
    }
  },
  methods: {
    // ---------- 查询 ----------
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      expenseClaimApi.queryList({
        ...this.cleanParams(),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    // 报表模式：KPI + 费用类型分布（走本页自己的 stat 接口，不跨 controller）
    loadStat() {
      expenseClaimApi.stat(this.cleanParams()).then(res => {
        this.stat = res || {};
        // 容器在报表模式下才渲染，等 DOM 出来再画
        this.$nextTick(() => this.renderChart());
      });
    },
    /** 费用类型分布饼图（数据源 = stat.typeRows，金额为 0 的分组不画） */
    renderChart() {
      const dom = this.$refs.typeChart;
      if (!dom) {
        return;
      }
      // 切走再切回时容器是新节点，旧实例绑在已被移除的 DOM 上，先销毁重建
      if (this.typeChart && this.typeChart.getDom() !== dom) {
        this.typeChart.dispose();
        this.typeChart = null;
      }
      if (!this.typeChart) {
        this.typeChart = markRaw(echarts.init(dom));
      }
      const data = this.typeRows
          .map(row => ({name: this.typeName(row.dimKey) || row.dimKey, value: Number(row.totalAmount || 0)}))
          .filter(item => item.value > 0);
      this.typeChart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} 元"},
        legend: {bottom: 0, type: "scroll"},
        series: [{
          type: "pie",
          radius: ["45%", "68%"],
          center: ["50%", "45%"],
          label: {formatter: "{b}\n{c} 元"},
          data: data,
        }],
      }, true);
    },
    /** 空值不提交，避免空串绑到后端 Integer 报错 */
    cleanParams() {
      const params = {};
      Object.keys(this.searchInfo).forEach(key => {
        const value = this.searchInfo[key];
        if (value === "" || value === null || value === undefined) {
          return;
        }
        params[key] = value;
      });
      // 只留查询条件，分页参数由调用方补
      delete params.pageNumber;
      delete params.pageSize;
      delete params.hasMore;
      return params;
    },
    resetSearch() {
      if (this.mode === "list") {
        this.searchInfo.pageNumber = 0;
        this.dataList = [];
        this.searchInfo.hasMore = true;
        this.queryList();
      } else {
        this.loadStat();
      }
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    // 不传参就是「切到另一个模式」
    switchMode(mode) {
      const next = mode || (this.mode === "list" ? "report" : "list");
      if (this.mode === next) {
        return;
      }
      this.mode = next;
      this.resetSearch();
    },
    // ---------- 展示 ----------
    /*
     * 匹配一律用 userCode，不是字典 id。
     * 单据里存的是 userCode（如 TRAVEL）—— 它是稳定的业务编码，
     * 而 id 是主键，重新导入字典就会变，存量单据立刻失去关联。
     */
    typeName(typeUserCode) {
      const type = this.typeList.find(item => item.userCode === typeUserCode);
      return type ? type.dictName : "";
    },
    typeTagType(typeUserCode) {
      const index = this.typeList.findIndex(item => item.userCode === typeUserCode);
      return index < 0 ? "info" : TYPE_COLORS[index % TYPE_COLORS.length];
    },
    statusName(status) {
      const hit = STATUS_OPTIONS.find(item => item.value === status);
      return hit ? hit.label : "—";
    },
    statusTagType(status) {
      return STATUS_TAG[status] || "info";
    },
    approvalName(status) {
      const hit = APPROVAL_OPTIONS.find(item => item.value === status);
      return hit ? hit.label : "未开始";
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "0.00";
      }
      return "¥ " + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    toDetail(data) {
      this.router.push(`/qywx/crm/reimbursement/detail?id=${data.id}`);
    },
    // 新增报销（详情页不带 id 即编辑模式）
    toCreate() {
      this.router.push("/qywx/crm/reimbursement/detail");
    }
  }
};
</script>

<style lang="scss" scoped>
.claim-list{
  height: 100%;
  display: flex;
  flex-direction: column;
  // 悬浮按钮的定位祖先
  position: relative;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
// 数据模式：一个 chip 显示当前模式，点一下切换
// ★ 用主题色：它不是普通查询条件，而是「换个视角看同一份数据」，颜色上区分开
.mode-chip{
  font-size: 12px;
  color: var(--el-color-primary);
}
// 类型 / 状态标签
.type-tag,
.status-tag{
  flex: none;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: var(--yq-font-size-mini-mobile);
}
.tag-primary{
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}
.tag-info{
  color: var(--el-color-info);
  background: var(--el-fill-color-light);
}
.tag-warning{
  color: var(--el-color-warning);
  background: var(--el-color-warning-light-9);
}
.tag-success{
  color: var(--el-color-success);
  background: var(--el-color-success-light-9);
}
.tag-danger{
  color: var(--el-color-danger);
  background: var(--el-color-danger-light-9);
}
.claim-item{
  :deep(.card-title){
    // 费用说明通常较长，右上角又有两个标签，允许折两行
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 图表卡片
.chart-card{
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #FFF;
  box-shadow: 0 0 10px var(--yq-shadow);
  .section-title{
    font-size: var(--yq-font-size-mini-mobile);
    font-weight: 600;
    color: var(--yq-font-color-base);
  }
  .chart-div{
    width: 100%;
    height: 240px;
  }
}
// 报表模式：整块可滚动
.report-body{
  flex: 1;
  overflow-y: auto;
  padding: 0 10px 10px;
  .stat-grid{
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding-top: 10px;
    .stat-card{
      background: #FFF;
      border-radius: 6px;
      padding: 10px 12px;
      box-shadow: 0 0 10px var(--yq-shadow);
      .label{
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-desc);
      }
      .value{
        margin-top: 4px;
        font-size: var(--yq-font-size-large-mobile);
        font-weight: 700;
        color: var(--yq-font-color-base);
        word-break: break-all;
        &.money{
          font-size: var(--yq-font-size-small-mobile);
        }
        &.warning{
          color: #e6a23c;
        }
        &.success{
          color: #67c23a;
        }
        .unit{
          margin-left: 2px;
          font-size: var(--yq-font-size-mini-mobile);
          font-weight: 400;
        }
      }
    }
  }
  .tip-bar{
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #FAFBFC;
    border: 1px solid var(--yq-border-color);
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--yq-font-color-desc);
    line-height: 1.7;
  }
  .report-title{
    margin: 14px 0 0;
    font-size: var(--yq-font-size-mini-mobile);
    font-weight: 600;
    color: var(--yq-font-color-base);
  }
  .report-empty{
    margin-top: 10px;
    padding: 20px;
    text-align: center;
    color: var(--yq-font-color-desc);
    background: #FFF;
    border-radius: 6px;
    box-shadow: 0 0 10px var(--yq-shadow);
  }
}
</style>
